/**
 * The words the process designer uses, in one place.
 *
 * The engine's vocabulary is accurate and almost unreadable: a template holds
 * *stages*, which are either *DataEntry* or *Approval*, holding *forms* and
 * *workflows*, which hold *roles*, while the request walks *statuses* of a
 * given *kind*. Seven nouns before the first click, and four of them mean
 * something narrower here than they do in ordinary English ("workflow" is one
 * signing step, not the process; "form" is one section of a form).
 *
 * Renaming the *models* is off the table -- that is a migration across every
 * table, URL and generated client for no functional gain (see CLAUDE.md). So
 * the rename happens here instead, at the surface: the code keeps saying
 * `Stage` and `Workflow`, and the person reading the screen sees "step" and
 * "sign-off". This module is the dictionary between the two, so the mapping is
 * decided once rather than drifting across thirty JSX files.
 *
 * Every string here is an English source string and therefore also its own
 * translation key (invariant 7) -- these are passed through `t()` at the call
 * site, never pre-translated here, so the extractor can still see them.
 */

/** The canonical user-facing noun for each engine concept. */
export const TERMS = {
  /** `Stage` — one position in the process. */
  stage: "Step",
  stagePlural: "Steps",
  /** `Form` — one block of questions inside a step. */
  section: "Section",
  sectionPlural: "Sections",
  /** `Workflow` — one signing step inside an approval step. */
  workflow: "Sign-off",
  workflowPlural: "Sign-offs",
  /** `WorkflowRole` — one named signature line inside a sign-off. */
  role: "Signature slot",
  rolePlural: "Signature slots",
  /** `RequestStatus` — a state a case sits in. */
  status: "State",
  statusPlural: "States",
  /** `Request` — one case walking the process. */
  request: "Case",
  requestPlural: "Cases",
} as const;

/**
 * What a step is for, in words rather than in `StageKind`.
 *
 * `DataEntry` and `Approval` are the two things a step can be, and the
 * distinction drives everything the engine does -- but the enum values read
 * like column names. These are the same two ideas said out loud.
 */
export const STAGE_KIND_LABEL: Record<string, string> = {
  DataEntry: "People fill it in",
  Approval: "People sign it off",
};

/** The same thing, short enough for a node badge or a chip. */
export const STAGE_KIND_SHORT: Record<string, string> = {
  DataEntry: "Fill in",
  Approval: "Sign off",
};

/** One line saying what each kind of step actually does. */
export const STAGE_KIND_HINT: Record<string, string> = {
  DataEntry:
    "Holds sections of the form. The case waits here until the sections that are required have been filled in and submitted.",
  Approval:
    "Holds sign-offs. The case waits here until every signature slot has been signed.",
};

/**
 * What a lifecycle state means to the engine.
 *
 * This is the single most misunderstood thing in the product, and the cause of
 * three production bugs: behaviour keys off `kind`, never off the name, so a
 * client may rename "Approval" to "На согласовании" and nothing breaks. The
 * designer has to make that legible, because an administrator who thinks the
 * name matters will eventually pick the wrong kind.
 */
export const STATUS_KIND_LABEL: Record<string, string> = {
  Draft: "Being written",
  DataEntry: "Being filled in",
  Approval: "Being signed off",
  Active: "Live",
  Closed: "Finished",
  Other: "Something else",
};

export const STATUS_KIND_HINT: Record<string, string> = {
  Draft:
    "Where a new case starts. Every process needs exactly one of these, and the case can be edited freely while it is here.",
  DataEntry: "The case is being filled in, but is no longer a free-form draft.",
  Approval:
    "Moving into this state is what opens the signature slots and starts the approvals. Only one state can mean this.",
  Active: "The work is authorised and under way.",
  Closed: "The case is over. Nothing further happens to it.",
  Other:
    "No special behaviour — a state of your own, such as suspended or on hold.",
};

/** How a signature is captured, said plainly. */
export const WORKFLOW_TYPE_LABEL: Record<string, string> = {
  Button: "A click in the app",
  EDS: "A digital certificate (NCALayer / eGov)",
  EHS: "A signature drawn on screen",
};

/** How the people who sign a slot are decided. */
export const ASSIGNMENT_LABEL: Record<string, string> = {
  ManualAny: "The requester picks anyone",
  ManualFromPool: "The requester picks from a group",
  AutoQuorum: "Everyone in a group is invited, some must sign",
  AutoAll: "Everyone in a group must sign",
  // The two derived modes read the org chart instead of a pool, so a process
  // configured once keeps working after a reorganisation.
  AutoManager: "The requester's manager",
  AutoUnitHead: "The head of an organisation unit",
};

/** The same, short enough for a chip on a node. */
export const ASSIGNMENT_SHORT: Record<string, string> = {
  ManualAny: "Anyone",
  ManualFromPool: "From a group",
  AutoQuorum: "Quorum",
  AutoAll: "Everyone",
  AutoManager: "Their manager",
  AutoUnitHead: "A unit head",
};

/** Who may fill a section in. */
export const FILL_PERMISSION_LABEL: Record<string, string> = {
  Anyone: "Anyone who can see the case",
  Requester: "Only the person who raised it",
  Group: "Members of a group",
};

/** Whether submitting a section takes a signature. */
export const SIGNATURE_KIND_LABEL: Record<string, string> = {
  NotRequired: "No signature",
  EDS: "A digital certificate",
  EHS: "A signature drawn on screen",
};

/**
 * Look a label up, falling back to the raw value.
 *
 * The choice lists come from the server, so a value added to a Django enum
 * without being added here must still render as *something* rather than
 * blank -- an unlabelled dropdown option is worse than an ugly one.
 */
export const labelFor = (
  table: Record<string, string>,
  value: string | null | undefined
): string => (value && table[value]) || value || "";
